import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import {  of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as loginActions from '../actions/login.actions';
import { IApiResult } from '../interfaces/api-result.interface';
import { IAppState } from '../interfaces/app-state.interface';
import {
  ILoginApiResponseFailure,
  ILoginApiResponseSuccess,
} from '../interfaces/login-api-response.interface';
import { ApolloService } from '../services/apollo.service';
import { LoginService } from '../services/login.service';
import { PollingService } from '../services/polling.service';
import { StringService } from '../services/string.service';

export const DEFAULT_REDIRECT_URL = 'launchpad';

@Injectable()
export class LoginEffects {
  public strings = null;

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginActions.LOGIN),
      debounceTime(300),
      map((action: loginActions.LoginAction) => action.payload),
      switchMap((payload) => {
        return this.loginService.login(payload.userName, payload.password).pipe(
          map((response) => {
            if (response.status === 200) {
              return {
                type: loginActions.LOGIN_SUCCESS,
                payload: response as IApiResult<ILoginApiResponseSuccess>,
              };
            } else {
              return {
                type: loginActions.LOGIN_FAILED,
                payload: response as IApiResult<ILoginApiResponseFailure>,
              };
            }
          }),
          catchError((error) => {
            if (error.status === 401) {
              return of({
                type: loginActions.LOGIN_FAILED,
                payload: {
                  status: null,
                  message: this.strings.loginDialogFailedLoginMessage,
                },
              });
            } else {
              return of({
                type: loginActions.LOGIN_FAILED,
                payload: {
                  status: null,
                  message: 'Error while logging in.',
                },
              });
            }
          }),
        );
      }),
    ),
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginActions.REFRESH_TOKEN),
      debounceTime(300),
      map((action: loginActions.RefreshTokenAction) => action.payload),
      switchMap((payload) => {
        return this.loginService.refreshToken(payload.token).pipe(
          map((response) => {
            if (response.status === 200) {
              return {
                type: loginActions.REFRESH_TOKEN_SUCCESS,
                payload: response as IApiResult<ILoginApiResponseSuccess>,
              };
            } else {
              return {
                type: loginActions.LOGIN_FAILED,
                payload: response as IApiResult<ILoginApiResponseFailure>,
              };
            }
          }),
          catchError((error) => {
            if (error.status === 401) {
              return of({
                type: loginActions.LOGIN_FAILED,
                payload: {
                  status: null,
                  message: this.strings.loginDialogFailedLoginMessage,
                },
              });
            } else {
              return of({
                type: loginActions.LOGIN_FAILED,
                payload: {
                  status: null,
                  message: 'Error while logging in.',
                },
              });
            }
          }),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginActions.LOGIN_SUCCESS),
      withLatestFrom(this.store$),
      tap(([action, _state]: [loginActions.LoginSuccessAction, IAppState]) => {
        this.loginService.setAccessToken(action.payload);
        this.pollingService.startPolling();
      }),
      mergeMap(([_action, state]: [loginActions.LoginSuccessAction, IAppState]) => {
        if (state.login.redirectUrl) {
          this.router.navigateByUrl(state.login.redirectUrl);
        } else {
          this.router.navigateByUrl(DEFAULT_REDIRECT_URL);
        }

        return [{ type: "crm" }];
      }),
    ),
  );

  refreshTokenSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginActions.REFRESH_TOKEN_SUCCESS),
        tap((action: loginActions.RefreshTokenActionSuccess) => {
          const expiration = moment().add(
            action.payload.expiresIn - LoginService.EXPIRES_IN_OFFSET_SECONDS,
            'seconds',
          );

          localStorage.setItem(
            LoginService.ACCESS_TOKEN_NAME,
            JSON.stringify({
              token: action.payload.accessToken,
              refreshToken: action.payload.refreshToken,
              expiration,
              personnelId: action.payload.PersonnelId,
              currentUser: action.payload.currentUser,
              firstName: action.payload.FirstName,
              lastName: action.payload.LastName,
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  revokeLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginActions.EXPIRE_LOGIN, loginActions.LOGOUT),
      switchMap(() => {
        if (typeof this.apolloService.closeWebSocketClient === 'function') {
          this.apolloService.closeWebSocketClient();
        }

        return this.loginService.removeAccessToken().pipe(
          map((response) => {
            if (response.status === 200) {
              return {
                type: loginActions.LOGOUT_SUCCESS,
                payload: response as IApiResult<ILoginApiResponseSuccess>,
              };
            } else {
              return {
                type: loginActions.LOGOUT_FAILED,
                payload: response as IApiResult<ILoginApiResponseFailure>,
              };
            }
          }),
          catchError((error) => {
            if (error.status === 401) {
              return of({
                type: loginActions.LOGOUT_FAILED,
                payload: {
                  status: null,
                  message: this.strings.loginDialogFailedLoginMessage,
                },
              });
            } else {
              return of({
                type: loginActions.LOGOUT_FAILED,
                payload: {
                  status: null,
                  message: 'Error while logging out.',
                },
              });
            }
          }),
        );
      }),
    ),
  );

  revokeLoginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginActions.LOGOUT_SUCCESS),
        tap(() => {
          this.router.navigateByUrl('login');
        }),
      ),
    { dispatch: false },
  );

  revokeLoginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginActions.LOGOUT_FAILED),
        tap(() => {
          this.router.navigateByUrl('login');
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store$: Store<IAppState>,
    private loginService: LoginService,
    private pollingService: PollingService,
    private stringService: StringService,
    private router: Router,
    private apolloService: ApolloService,
  ) {
    this.stringService.strings$.subscribe((strings) => (this.strings = strings));
  }
}
